

import React, { useRef, useState } from "react";
import { useTheme } from "./ThemeProvider";
import ProductImageFallback from "./ProductImageFallback";
import PriceBadge from "./PriceBadge";
import QuantityControl from "./QuantityControl";
import TableActionButtons from "./TableActionButtons";
import { useCart } from "../../components/pos/contexts/cartContext";
import { showErrorToast } from "../../utils/toast";
import { playScanErrorSound, playScanSuccessSound } from "../../utils/scanSounds";


const ProductCard = ({
    product,
    onEdit,      // function(product)
    onDelete,    // function(product)
    showActionsOnly = false, // if true, only show edit/delete icons (for product grid)
    massEditMode = false,
    viewMode = "grid",
    showAddToCart = true,
    showEdit = false,
    onIncrease, // for inventory/other context
    onDecrease, // for inventory/other context
    uploadsBaseURL,
    mode = "pos", // "pos" or "inventory"
    showOutOfStockLabel = true, // NEW: control out of stock label
    showBranchWarehouseLabels = true, // NEW: control branch/warehouse labels in non-inventory mode
    showUnitType = false, // NEW: control unit type display under description
    unitType,
    showQuantityControl = true,
    disableIncrease = false,
    disableDecrease = false,
    onPreviewImage,
    children,
}) => {
    const cardRef = useRef(null);
    let currentTheme = { color: '#6366f1' };
    try {
        const theme = useTheme();
        if (theme && theme.currentTheme) currentTheme = theme.currentTheme;
    } catch (e) {
        // Intentionally left blank: fallback theme is already set
    }
    const [bulkQty, setBulkQty] = useState("");
    const [imageError, setImageError] = useState(false);

    // Always call useCart at the top level
    const cart = useCart();

    let currentCartQty = 0;
    let remainingStock = product.stock;
    let handleIncrease = onIncrease;
    let handleDecrease = onDecrease;
    let handleBulkAdd = undefined;
    let useCartAvailable = false;

    if (showAddToCart && cart) {
        useCartAvailable = true;
        currentCartQty = cart.getItemQuantity(product.id);
        remainingStock = product.stock - currentCartQty;
        handleIncrease = () => {
            if (remainingStock > 0) {
                const wasAdded = cart.addToCart(product, 1);
                if (wasAdded) {
                    playScanSuccessSound();
                } else {
                    playScanErrorSound();
                }
            } else {
                playScanErrorSound();
            }
        };
        handleDecrease = () => {
            if (currentCartQty > 0) {
                cart.addToCart(product, -1);
            }
        };
        handleBulkAdd = () => {
            const requestedQty = Number(bulkQty) || 0;
            if (requestedQty <= 0) return;
            if (requestedQty > remainingStock) {
                playScanErrorSound();
                showErrorToast(`Only ${remainingStock} left in stock`);
                return;
            }
            const wasAdded = cart.addToCart(product, requestedQty);
            if (wasAdded) {
                playScanSuccessSound();
                setBulkQty("");
            } else {
                playScanErrorSound();
            }
        };
    } else {
        // For inventory/other context, use props
        currentCartQty = product.stock;
        remainingStock = product.stock;
    }

    return (
        <div
            ref={cardRef}
            className="bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 p-4 flex flex-col transition-all duration-200 border border-gray-200 relative"
            style={{
                background: product.stock > 0 ? 'var(--color-background)' : 'var(--color-muted-bg)',
                cursor: product.stock > 0 ? 'pointer' : 'not-allowed',
                paddingBottom: massEditMode ? '92px' : undefined // add space for fixed controls
            }}
        >
            {/* Product Image with Stock Badge */}

            <div
                className={`aspect-square w-full bg-gray-100 rounded-lg overflow-hidden mb-3 flex items-center justify-center relative ${onPreviewImage ? "cursor-zoom-in" : ""}`}
                onClick={onPreviewImage}
                role={onPreviewImage ? "button" : undefined}
                tabIndex={onPreviewImage ? 0 : undefined}
                onKeyDown={onPreviewImage ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPreviewImage();
                    }
                } : undefined}
                aria-label={onPreviewImage ? `Preview ${product.name} image` : undefined}
            >
                {/* Price Badge in upper left */}
                <div className="absolute top-2 left-2 z-20">
                    <PriceBadge price={product.price} />
                </div>
                {product.image && !imageError ? (
                    <img
                        src={product.image}
                        alt={product.name}
                        className="object-cover w-full h-full"
                        loading="lazy"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <ProductImageFallback className="w-full h-full" aria-label="No product image" />
                )}
                {product.stock <= 0 && showOutOfStockLabel && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
                        <span className="text-white text-xs font-bold">{mode === "inventory" ? "No stock available" : "Out of Stock"}</span>
                    </div>
                )}
            </div>

            {/* Product Name */}
            <h3 className="font-bold text-base truncate text-center mb-1" style={{ color: 'var(--color-text)' }}>
                {product.name}
            </h3>

            {/* Product Description (always rendered, no clamp, no flex, no min height) */}
            <div className="mb-1">
                <p className="text-xs text-center w-full" style={{ color: 'var(--color-muted)' }}>
                    {product.description || '\u00A0'}
                </p>
                {showUnitType && (
                    <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {product.category && (
                            <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100"
                            >
                                {product.category}
                            </span>
                        )}
                        <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                        >
                            {unitType || '-'}
                        </span>
                    </div>
                )}
            </div>

            {/* Stock Badge under description (always show in inventory mode) */}
            <div className="flex justify-center mb-2">
                {mode === "inventory" ? (
                    product.stock > 0 ? (
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></svg>
                            {product.stock} in stock
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                            No stock
                        </span>
                    )
                ) : showBranchWarehouseLabels ? (
                    <div className="flex flex-col gap-1 text-center w-full">
                        {/* Branch Stock */}
                        {product.stock > 0 ? (
                            <span className="inline-flex items-center justify-center gap-1 bg-green-50 text-green-700 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></svg>
                                Branch: {product.stock} units
                            </span>
                        ) : (
                            <span className="inline-flex items-center justify-center gap-1 bg-red-50 text-red-600 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                                Branch: Out
                            </span>
                        )}
                        {/* Warehouse Stock */}
                        {product.warehouse_stock > 0 ? (
                            product.warehouse_stock < 10 ? (
                                <span className="inline-flex items-center justify-center gap-1 bg-orange-50 text-orange-700 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 8v8m0 4v.01M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /></svg>
                                    Warehouse: {product.warehouse_stock} units
                                </span>
                            ) : (
                                <span className="inline-flex items-center justify-center gap-1 bg-green-50 text-green-700 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></svg>
                                    Warehouse: {product.warehouse_stock} units
                                </span>
                            )
                        ) : (
                            <span className="inline-flex items-center justify-center gap-1 bg-red-50 text-red-600 text-[11px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                                Warehouse: Out
                            </span>
                        )}
                    </div>
                ) : null}
            </div>

            {/* Action Buttons (edit/delete) for product grid, or quantity control for POS/inventory */}
            {!massEditMode ? (
                showActionsOnly ? (
                    <div className="flex items-center gap-2 mt-auto justify-center">
                        <TableActionButtons
                            canEdit={!!onEdit}
                            canDelete={!!onDelete}
                            canToggle={false}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    </div>
                ) : showQuantityControl ? (
                    <div className="flex items-center gap-2 mt-auto">
                        {showAddToCart && useCartAvailable ? (
                            currentCartQty === 0 ? (
                                <button
                                    onClick={handleIncrease}
                                    disabled={mode !== "inventory" && remainingStock <= 0}
                                    className="w-full bg-primary text-white font-medium py-1 px-2 rounded-md hover:bg-primary-dark transition-colors focus:outline-none disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-xs"
                                    style={{ marginTop: 'auto', minHeight: '32px' }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.272 1.017M7.5 14.25a3 3 0 106 0m-6 0a3 3 0 016 0m-6 0H5.25a2.25 2.25 0 01-2.197-1.772L2.25 3m5.25 11.25l1.5-6.75m0 0l1.5 6.75m-1.5-6.75h6.75a1.125 1.125 0 011.1 1.372l-1.35 6.053a2.25 2.25 0 01-2.197 1.775H7.5z" />
                                    </svg>
                                    Add to Cart
                                </button>
                            ) : (
                                <div className="w-full flex justify-center items-center">
                                    <QuantityControl
                                        quantity={currentCartQty}
                                        onIncrease={handleIncrease}
                                        onDecrease={handleDecrease}
                                        disableIncrease={disableIncrease || (mode !== "inventory" && remainingStock <= 0)}
                                        disableDecrease={disableDecrease}
                                    />
                                    {children}
                                </div>
                            )
                        ) : (
                            <div className="w-full flex justify-center items-center">
                                <QuantityControl
                                    quantity={currentCartQty}
                                    onIncrease={handleIncrease}
                                    onDecrease={handleDecrease}
                                    disableIncrease={disableIncrease || (mode !== "inventory" && remainingStock <= 0)}
                                    disableDecrease={disableDecrease}
                                />
                                {children}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="mt-auto" />
                )
            ) : (
                <>
                    {/* Fixed bottom controls for massEditMode */}
                    <div className="absolute inset-x-0 bottom-0 z-10 px-4 pb-4">
                        <div className="rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-[0_12px_28px_rgba(15,23,42,0.10)] backdrop-blur">
                            <div className="mb-1.5 flex items-center justify-between px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                <span>Quick Qty</span>
                                <span>{remainingStock > 0 ? `${remainingStock} left` : "Out"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max={remainingStock}
                                    value={bulkQty}
                                    onChange={(e) => setBulkQty(e.target.value)}
                                    className="h-10 w-[86px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-center text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--color-primary)] focus:bg-white focus:ring-4 focus:ring-[color-mix(in_srgb,var(--color-primary)_14%,white)] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                                    placeholder="0"
                                    disabled={remainingStock <= 0}
                                    title={remainingStock <= 0 ? "Product out of stock" : ""}
                                />
                                <button
                                    type="button"
                                    onClick={handleBulkAdd}
                                    disabled={Number(bulkQty) <= 0 || remainingStock <= 0}
                                    className="flex h-10 flex-1 items-center justify-center rounded-xl px-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                                    style={Number(bulkQty) > 0 && remainingStock > 0 ? { background: 'var(--color-primary)' } : undefined}
                                    onMouseOver={Number(bulkQty) > 0 && remainingStock > 0 ? (event) => {
                                        event.currentTarget.style.background = 'color-mix(in srgb, var(--color-primary) 82%, black)';
                                    } : undefined}
                                    onMouseOut={Number(bulkQty) > 0 && remainingStock > 0 ? (event) => {
                                        event.currentTarget.style.background = 'var(--color-primary)';
                                    } : undefined}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {/* Show edit button if enabled */}
            {showEdit && (
                <button
                    onClick={() => onEdit && onEdit(product)}
                    className="mt-2 w-full text-white font-semibold py-2 rounded-lg transition-colors focus:outline-none"
                    style={{
                        background: currentTheme.color,
                        // Use a slightly darkened color for hover
                        transition: 'background 0.2s',
                    }}
                    onMouseOver={e => e.currentTarget.style.background = require('tinycolor2')(currentTheme.color).darken(10).toString()}
                    onMouseOut={e => e.currentTarget.style.background = currentTheme.color}
                >
                    Edit
                </button>
            )}
        </div>
    );
};

export default ProductCard;
